import { ReactNode, useState } from "react";

import { Alert } from "../../../components/Alert/Alert";
import { Button } from "../../../components/Button/Button";
import { Input } from "../../../components/Input/Input";
import { SegmentedToggle } from "../../../components/SegmentedToggle/SegmentedToggle";
import { Textarea } from "../../../components/Textarea/Textarea";
import styles from "./BotsSettings.module.css";

/** The locales the platform already ships a built-in list of stems for. */
const BUILT_IN_LOCALES = "ru en pt de es fr tr pl";
const LOCALE_PATTERN = /^[a-z]{2,5}$/;

/** One name per line, trimmed, blanks dropped. The dedup waits for blur. */
function splitNames(text: string): string[] {
  return text
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line !== "");
}

/**
 * What a finished list is normalised to. The dedup is CASE SENSITIVE on
 * purpose: the built-in generator itself hands out `Anna` and `anna` as two
 * different handles, so folding them together would quietly halve a pool the
 * operator counted out by hand.
 */
function normalizeNames(text: string): { names: string[]; removed: number } {
  const lines = splitNames(text);
  const seen = new Set<string>();
  const names: string[] = [];
  for (const line of lines) {
    if (seen.has(line)) continue;
    seen.add(line);
    names.push(line);
  }
  return { names, removed: lines.length - names.length };
}

const plural = (n: number, one: string, many: string) => (n === 1 ? one : many);

/**
 * `bots.names.custom` — a locale's finished names, edited as text because
 * there will be hundreds of them and they arrive from a spreadsheet in one
 * paste. One locale is open at a time; the rest sit in the toggle above.
 *
 * It lives here, exported, rather than inside a settings section, because it
 * answers "where does a bot's handle come from" — a question the Source tab
 * asks, next to the table that can replace it — and not "how does a bot
 * behave". Its own module for the same reason: the two tabs that could mount
 * it must not have to import one another.
 *
 * Mockup: config-bots-names.html, sections 2 and 3.
 */
export function CustomNamesEditor({
  value,
  onChange,
  targetMembers,
}: {
  value: Record<string, string[]>;
  onChange: (next: Record<string, string[]>) => void;
  /** `bots.population.target_members` — what a pool has to cover. */
  targetMembers: number;
}) {
  const locales = Object.keys(value).sort();
  const [selected, setSelected] = useState("");
  const [adding, setAdding] = useState(false);
  const [code, setCode] = useState("");
  const [codeError, setCodeError] = useState<ReactNode>(null);
  // The text being typed is held apart from the config: a line the operator is
  // halfway through writing must survive the trip through the parsed array.
  const [draft, setDraft] = useState<{
    locale: string;
    text: string;
    removed: number;
  } | null>(null);

  // The open locale can disappear under us — removed here, or replaced by a
  // discard on the page — so it is resolved on every render, not remembered.
  const active = locales.includes(selected) ? selected : (locales[0] ?? "");
  const names = value[active] ?? [];
  const onDraft = draft?.locale === active;
  const text = onDraft ? draft.text : names.join("\n");
  const removed = onDraft ? draft.removed : 0;

  const closeAdd = () => {
    setAdding(false);
    setCode("");
    setCodeError(null);
  };

  const addLocale = () => {
    const lc = code.trim().toLowerCase();
    if (!LOCALE_PATTERN.test(lc)) {
      setCodeError("Two to five letters, lowercase.");
      return;
    }
    if (locales.includes(lc)) {
      setCodeError(
        <>
          A list for <code>{lc}</code> already exists.
        </>,
      );
      return;
    }
    onChange({ ...value, [lc]: [] });
    setSelected(lc);
    closeAdd();
  };

  const addButton = (
    <Button size="sm" variant="secondary" onClick={() => setAdding(true)}>
      + Locale
    </Button>
  );

  const addForm = (
    <div className={styles.localeAdd}>
      <Input
        value={code}
        onChange={(e) => {
          setCode(e.target.value.toLowerCase());
          setCodeError(null);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            addLocale();
          }
        }}
        placeholder="de"
        aria-label="Locale code"
        aria-invalid={codeError ? true : undefined}
        className={codeError ? styles.localeInvalid : undefined}
        maxLength={5}
      />
      <Button size="sm" variant="secondary" onClick={addLocale}>
        Add
      </Button>
      <button type="button" className={styles.linkBtn} onClick={closeAdd}>
        Cancel
      </button>
    </div>
  );

  return (
    <>
      {locales.length === 0 && !adding ? (
        <div className={styles.emptyBox}>
          <span className={styles.emptyText}>
            No custom lists — every locale uses the built-in generator.
          </span>
          {addButton}
        </div>
      ) : (
        <div className={styles.namesHead}>
          {locales.length > 0 && (
            <SegmentedToggle
              aria-label="Custom name lists"
              value={active}
              onChange={setSelected}
              options={locales.map((lc) => ({
                value: lc,
                label: lc,
                count: (value[lc] ?? []).length,
              }))}
            />
          )}
          {adding ? addForm : addButton}
        </div>
      )}

      {codeError ? (
        <div className={styles.fieldError}>{codeError}</div>
      ) : (
        adding && (
          <div className={styles.helper}>
            Two to five letters, lowercase. Built-in lists exist for{" "}
            <code>{BUILT_IN_LOCALES}</code> — a custom list for one of them
            replaces it.
          </div>
        )
      )}

      {active && (
        <>
          <Textarea
            className={styles.namesArea}
            aria-label={`Names for ${active}`}
            value={text}
            rows={10}
            spellCheck={false}
            onChange={(e) => {
              setDraft({ locale: active, text: e.target.value, removed: 0 });
              onChange({ ...value, [active]: splitNames(e.target.value) });
            }}
            onBlur={() => {
              const clean = normalizeNames(text);
              setDraft({
                locale: active,
                text: clean.names.join("\n"),
                removed: clean.removed,
              });
              onChange({ ...value, [active]: clean.names });
            }}
          />
          <div className={styles.namesFoot}>
            <span>
              <span className={styles.count}>
                {names.length} {plural(names.length, "name", "names")}
              </span>{" "}
              · one per line
              {removed > 0 && (
                <>
                  {" "}
                  ·{" "}
                  <span className={styles.countWarn}>
                    {removed} {plural(removed, "duplicate", "duplicates")}{" "}
                    removed
                  </span>
                </>
              )}
            </span>
            <button
              type="button"
              className={`${styles.linkBtn} ${styles.linkBtnDanger}`}
              onClick={() => {
                const next = { ...value };
                delete next[active];
                onChange(next);
                setSelected("");
                setDraft(null);
              }}
            >
              Remove the {active} list
            </button>
          </div>
          <div className={styles.helper}>
            The <code>{active}</code> list replaces the built-in generator for
            that locale entirely — these names are used verbatim. The digits
            suffix still applies on top, and a name already taken in the project
            is never handed out twice.
          </div>
          {names.length < targetMembers && (
            <Alert
              variant="warn"
              title={`${names.length} ${plural(names.length, "name", "names")} is a small pool`}
            >
              A guild fills to <code>target_members</code> = {targetMembers},
              and this list has to cover every guild of the locale at once.
              Below the target size the digits suffix takes over and the roster
              starts reading as generated. The built-in generator produces
              several hundred per locale.
            </Alert>
          )}
        </>
      )}

      <div className={styles.helper}>
        A locale with neither a custom list nor a built-in one falls back to the
        default locale's.
      </div>
    </>
  );
}
